import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Trophy, Sparkles, Globe, Check, ArrowRight, Tv, Users, Eye, Layers,
  AlertCircle, Wrench,
} from 'lucide-react';
import { AssistedDeploymentModal } from '../components/AssistedDeploymentModal';
import { useCart } from '../contexts/CartContext';

const VARIANTS = [
  { id: 'scroller', label: 'Team/Spot Scroller' },
  { id: 'board', label: 'Team/Spot Board' },
];

const PRICING = [
  {
    id: 'starter',
    widgetFamily: 'team-spot-owner',
    price: 79,
    title: 'Starter Widget',
    blurb: 'A clean, ready-to-use owner display with LAB21G branding.',
    features: [
      'Web-hosted widget',
      'Football, basketball & baseball support',
      'LAB21G branding included',
      'OBS / browser source compatible',
    ],
    needsVariant: true,
    skuByVariant: {
      scroller: { sku: 'team-spot-starter-scroller', fullName: 'Team / Spot Owner — Starter (Scroller, LAB21G branded)' },
      board: { sku: 'team-spot-starter-board', fullName: 'Team / Spot Owner — Starter (Board, LAB21G branded)' },
    },
    accent: 'green',
  },
  {
    id: 'nonbranded',
    widgetFamily: 'team-spot-owner',
    price: 149,
    title: 'Non-Branded Widget',
    blurb: 'Clean professional display — no LAB21G branding shown.',
    features: [
      'Web-hosted widget',
      'Football, basketball & baseball support',
      'No LAB21G branding',
      'OBS / browser source compatible',
    ],
    needsVariant: true,
    skuByVariant: {
      scroller: { sku: 'team-spot-nonbranded-scroller', fullName: 'Team / Spot Owner — Non-Branded (Scroller)' },
      board: { sku: 'team-spot-nonbranded-board', fullName: 'Team / Spot Owner — Non-Branded (Board)' },
    },
    accent: 'cyan',
    featured: true,
  },
  {
    id: 'custom',
    sku: 'team-spot-full-custom',
    widgetFamily: 'team-spot-owner',
    price: 229,
    title: 'Full Custom Widget',
    blurb: 'Both Scroller and Board, your branding, your style.',
    features: [
      'Both Team/Spot Scroller and Team/Spot Board',
      'Football, basketball & baseball support',
      'Your own branding · custom colors, layout, titles, logos',
      'No LAB21G branding',
      'Other sports / formats available on request (setup fee may apply)',
    ],
    fullName: 'Team / Spot Owner — Full Custom (Scroller + Board)',
    accent: 'green',
  },
];

const BENEFITS = [
  { icon: Users, title: 'Keep buyers engaged', text: 'Owners and claimed teams visible on screen — no more "did I get the Bills?" mid-break.' },
  { icon: Eye, title: 'Easy to see at a glance', text: 'Cleaner than scrolling a spreadsheet on camera. Looks like a pro show.' },
  { icon: Layers, title: 'Built for breakers', text: 'Pinned breaks, live auctions, and live break formats — all supported.' },
  { icon: Tv, title: 'Stream-ready', text: 'Web-hosted URL. Drop it into OBS, Streamlabs, TikTok LIVE Studio, Whatnot — done.' },
];

const setIntent = (label) => {
  try {
    sessionStorage.setItem('lab21g.contactIntent', label);
  } catch (e) { /* sessionStorage unavailable - safe to ignore */ }
};

export const TeamSpotOwnerWidget = () => {
  const [pendingItem, setPendingItem] = useState(null);
  const [replacingLineId, setReplacingLineId] = useState(null);
  const [variantChoices, setVariantChoices] = useState({
    starter: 'scroller',
    nonbranded: 'scroller',
  });
  const { items } = useCart();

  const resolveSku = (tier) => {
    if (tier.needsVariant) return tier.skuByVariant[variantChoices[tier.id]];
    return { sku: tier.sku, fullName: tier.fullName };
  };

  const startAdd = (tier) => {
    const { sku, fullName } = resolveSku(tier);
    const existing = items.find((i) => i.widgetFamily === tier.widgetFamily);
    if (existing && existing.sku === sku) {
      toast.info('Already in your cart', {
        description: `${fullName} is already in your cart.`,
        duration: 2400,
      });
      return;
    }
    setReplacingLineId(existing ? existing.lineId : null);
    setPendingItem({
      sku,
      name: fullName,
      price: tier.price,
      widgetFamily: tier.widgetFamily,
      defaultAssisted: existing?.assistedDeployment || false,
      replacingName: existing?.name || null,
    });
  };

  const scrollToPricing = () => {
    document.querySelector('[data-testid="team-spot-pricing"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const closeModal = () => {
    setPendingItem(null);
    setReplacingLineId(null);
  };

  return (
    <div data-testid="team-spot-owner-page">
      <AssistedDeploymentModal pendingItem={pendingItem} replacingLineId={replacingLineId} onClose={closeModal} />

      {/* HERO */}
      <section className="hero-gradient py-20 px-4 sm:px-6 lg:px-8 border-b border-subtle">
        <div className="max-w-5xl mx-auto text-center">
          <span className="eyebrow eyebrow-blue" data-testid="team-spot-eyebrow">Stream Widget · Team / Spot Owner</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 mb-5 tracking-tight leading-[1.05]" data-testid="team-spot-headline">
            <span className="text-strong">Team / Spot Owner </span>
            <span className="text-gradient-brand">Widget</span>
          </h1>
          <p className="text-base sm:text-lg text-muted max-w-2xl mx-auto mb-9">
            A professional, web-hosted display for sports card breakers and live sellers. Show team owners,
            spot owners, claimed teams, and claimed spots on stream — clean, on-brand, no spreadsheets.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button type="button" onClick={scrollToPricing} className="btn-primary px-6 py-3 inline-flex items-center space-x-2" data-testid="team-spot-cta-get-started">
              <span>Get Started</span><ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="/#contact"
              onClick={() => setIntent('Team / Spot Owner Widget — Custom widget request')}
              className="btn-secondary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="team-spot-cta-custom"
            >
              <Sparkles className="w-4 h-4" /><span>Request Custom Widget</span>
            </a>
            <button type="button" onClick={scrollToPricing} className="btn-secondary px-6 py-3 inline-flex items-center space-x-2" data-testid="team-spot-cta-options">
              <Layers className="w-4 h-4" /><span>View Widget Options</span>
            </button>
          </div>
          <div className="mt-7 text-xs text-faded inline-flex items-center gap-2 flex-wrap justify-center">
            <span className="px-2.5 py-1 rounded border border-default">Web-hosted</span>
            <span className="px-2.5 py-1 rounded border border-default">OBS · Streamlabs · TikTok LIVE</span>
            <span className="px-2.5 py-1 rounded border border-default">eBay LIVE · Whatnot</span>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="team-spot-benefits">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow">Why breakers love it</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              A cleaner break is a better break.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {BENEFITS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="card-surface p-6 flex space-x-4" data-testid={`benefit-card-${i}`}>
                <div className="shrink-0">
                  <Icon className="w-6 h-6 accent-green" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-strong mb-1">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-y border-subtle" data-testid="team-spot-pricing">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow eyebrow-blue">Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              Pick the version that fits your show.
            </h2>
            <p className="text-sm text-muted mt-3 max-w-xl mx-auto">
              All tiers are web-hosted. Self-setup, or add Assisted Deployment at checkout if you want a hand.
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {PRICING.map((tier) => (
              <div
                key={tier.id}
                className={`card-surface p-7 relative ${tier.featured ? 'border-emphasis' : ''}`}
                data-testid={`team-spot-tier-${tier.id}`}
                style={tier.featured ? { boxShadow: '0 0 30px -8px rgba(0, 240, 255, 0.25)' } : {}}
              >
                {tier.featured && (
                  <div className="absolute -top-3 left-7 brand-green text-[10px] uppercase tracking-widest px-2.5 py-1 rounded border border-default font-semibold" style={{ background: 'var(--bg-base)' }}>
                    Most popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-strong mb-1">{tier.title}</h3>
                <p className="text-xs text-muted mb-5">{tier.blurb}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-strong" data-testid={`team-spot-tier-price-${tier.id}`}>${tier.price}</span>
                  <span className="text-sm text-faded ml-1">one-time</span>
                </div>
                <ul className="space-y-2.5 mb-5">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start space-x-2 text-sm text-default">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tier.accent === 'cyan' ? 'accent-cyan' : 'accent-green'}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {tier.needsVariant && (
                  <div className="mb-6" data-testid={`team-spot-variant-${tier.id}`}>
                    <div className="text-[11px] uppercase tracking-widest text-faded font-semibold mb-2">Choose one</div>
                    <div className="grid grid-cols-2 gap-2">
                      {VARIANTS.map((v) => {
                        const selected = variantChoices[tier.id] === v.id;
                        return (
                          <button
                            key={v.id}
                            type="button"
                            onClick={() => setVariantChoices((p) => ({ ...p, [tier.id]: v.id }))}
                            className={`px-3 py-2 rounded border text-sm font-medium transition-colors ${
                              selected ? 'border-emphasis text-strong' : 'border-subtle text-muted hover:border-default hover:text-strong'
                            }`}
                            style={selected ? { background: 'rgba(57, 255, 20, 0.08)' } : {}}
                            data-testid={`team-spot-variant-${tier.id}-${v.id}`}
                          >
                            {v.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {tier.id === 'custom' && (
                  <div className="mb-6 p-3 rounded border border-subtle" style={{ background: 'rgba(0, 240, 255, 0.04)' }} data-testid={`team-spot-includes-${tier.id}`}>
                    <div className="text-[11px] uppercase tracking-widest text-faded font-semibold mb-1">Includes</div>
                    <div className="text-sm text-strong font-medium">Scroller + Board</div>
                    <div className="text-xs text-muted mt-0.5">Both are yours — use either, anytime.</div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => startAdd(tier)}
                  className={`block w-full text-center px-5 py-3 rounded-md text-sm font-semibold transition-colors ${
                    tier.featured ? 'btn-primary' : 'btn-secondary'
                  }`}
                  data-testid={`team-spot-tier-cta-${tier.id}`}
                >
                  Get this option
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED / AVAILABLE */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="team-spot-included">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-surface p-7">
            <div className="flex items-center space-x-3 mb-5">
              <Trophy className="w-6 h-6 accent-green" />
              <h3 className="text-xl font-bold text-strong tracking-tight">Included by default</h3>
            </div>
            <ul className="space-y-2.5">
              {[
                'Football team / spot displays',
                'Basketball team / spot displays',
                'Baseball team / spot displays',
                'Web-hosted setup',
                'OBS / browser source compatibility',
              ].map((f) => (
                <li key={f} className="flex items-start space-x-2 text-sm text-default">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 accent-green" /><span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="card-surface p-7">
            <div className="flex items-center space-x-3 mb-5">
              <Wrench className="w-6 h-6 accent-cyan" />
              <h3 className="text-xl font-bold text-strong tracking-tight">Available upon request</h3>
            </div>
            <ul className="space-y-2.5 mb-5">
              {[
                'Other sports',
                'Custom team lists',
                'Custom spot formats',
                'Special layouts',
                'Additional branding or style changes',
              ].map((f) => (
                <li key={f} className="flex items-start space-x-2 text-sm text-default">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 accent-cyan" /><span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted leading-relaxed">
              Other sports and custom formats may require an additional setup fee depending on the scope of the request.
            </p>
          </div>
        </div>

        {/* In-development note */}
        <div className="max-w-6xl mx-auto mt-6 p-4 rounded border border-default flex items-start space-x-3" style={{ background: 'rgba(0, 240, 255, 0.04)' }} data-testid="team-spot-dev-note">
          <AlertCircle className="w-5 h-5 accent-cyan shrink-0 mt-0.5" />
          <p className="text-sm text-default leading-relaxed">
            <strong className="text-strong">Pick Your Division support is currently in development</strong> and is not included at this time.
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 hero-gradient border-t border-subtle" data-testid="team-spot-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-strong tracking-tight">
            Ready to upgrade your LIVE break display?
          </h2>
          <p className="text-base text-muted mb-8">
            Let LAB21G build your Team / Spot Owner Widget and give your stream a cleaner,
            more professional look.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            <button type="button" onClick={scrollToPricing} className="btn-primary px-6 py-3 inline-flex items-center space-x-2" data-testid="team-spot-final-start">
              <span>Get Started</span><ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="/#contact"
              onClick={() => setIntent('Team / Spot Owner Widget — Custom widget request')}
              className="btn-secondary px-6 py-3 inline-flex items-center space-x-2"
              data-testid="team-spot-final-custom"
            >
              <Sparkles className="w-4 h-4" /><span>Request Custom Widget</span>
            </a>
            <Link to="/" className="btn-secondary px-6 py-3 inline-flex items-center space-x-2" data-testid="team-spot-final-back">
              <span>Back to all widgets</span>
            </Link>
          </div>
          <p className="text-sm text-faded tracking-wide">
            <span className="text-strong">Built for breakers.</span> Powered by <span className="text-gradient-brand font-semibold">LAB21G</span>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default TeamSpotOwnerWidget;
